import React, { useState } from 'react';
import {
  TextField,
  Datagrid,
  usePermissions,
  Filter,
  TextInput,
  ListController,
  ListContextProvider,
} from 'react-admin';
import { ListView } from '~/layout';
import { withStyles, createStyles, makeStyles } from '@material-ui/core/styles';
import { NoPermissions } from '~/components/ra';
import { Loader } from '~/components';
import { SelectInput } from 'react-admin';
import ApplicationRuleCreate from './components/ApplicationRuleCreate';
import ApplicationRuleEdit from './components/ApplicationRuleEdit';
import { ApplicationRule } from '~/types/ApplicationRules';
import { useTranslate } from 'react-admin';
import { Typography } from '@material-ui/core';
import { FunctionField } from 'react-admin';
import TenantLicensePaywall from '~/components/TenantLicensePaywall';
import { useCheckAppUpdateLicence } from '~/hooks/useCheckTenantLicense';
// import CircularProgress from '@material-ui/core/CircularProgress';

const label = (key: string) => `resources.applicationRules.fields.${key}`;

const listBodyStyles = (theme: any) => {
  return createStyles({
    content: {
      fontSize: 14,
      paddingLeft: 16,
      paddingRight: 16,
      [theme.breakpoints.up('sm')]: {
        paddingLeft: 24,
        paddingRight: 24,
      },
      paddingTop: 16,
      paddingBottom: 16,
    },
  });
};

const ApplicationRulesList = (props: any) => {
  const { permissions } = usePermissions();
  const classes = useStyles();
  const translate = useTranslate();
  const t = (key: string): string => translate(`resources.applicationRules.${key}`);
  const { isFeatureEnabled, isLoading } = useCheckAppUpdateLicence();

  if (!permissions) return null;
  if (!permissions?.includes('externalApplication.list')) return <NoPermissions />;
  if (isLoading) return <Loader />;
  if (!isFeatureEnabled) return <TenantLicensePaywall />;
  return (
    <ListController {...props}>
      {(controllerProps: any) => {
        return (
          <ListContextProvider value={controllerProps}>
            <ListView
              title={
                <div className={classes.headline}>
                  <Typography variant='h2'>{t('name')}</Typography>
                </div>
              }
              filters={<ApplicationRulesFilter />}
              {...controllerProps}
              pagination={controllerProps.loading || controllerProps.total === 0 ? null : undefined}
              bulkActionButtons={false}
              actions={<ApplicationRuleCreate />}
            >
              <ListBody />
            </ListView>
          </ListContextProvider>
        );
      }}
    </ListController>
  );
};

const ListBody = withStyles(listBodyStyles)((props: any) => {
  const translate = useTranslate();
  const t = (key: string): string => translate(label(key));

  const [showData, setShowData] = useState<ApplicationRule | null>(null);
  const handleOpen = (record: ApplicationRule) => {
    setShowData(record);
  };
  const handleClose = () => setShowData(null);
  const { total, loading, classes } = props;
  if (loading) {
    return <div className={classes.content}>{t('searchInProgress')}</div>;
  } else if (total === 0) {
    return <div className={classes.content}>{t('noResultsFound')}</div>;
  }
  return (
    <>
      <ApplicationRuleEdit record={showData} onClose={handleClose} />
      <Datagrid
        {...props}
        rowClick={(_id: any, _basePath: any, record: ApplicationRule) => handleOpen(record)}
        size='small'
        selectedIds={null}
      >
        <TextField source='applicationId' label={label('applicationId')} sortable={false} />

        <FunctionField
          source='platform'
          label={label('platform')}
          sortable={false}
          render={(record: any) => (record?.platform ? t(record?.platform) : record?.platform)}
        />
        <TextField
          source='minRequiredVersion'
          label={label('minRequiredVersion')}
          sortable={false}
        />
        <TextField
          source='recommendedVersion'
          label={label('recommendedVersion')}
          sortable={false}
        />
        <TextField source='link' label={label('link')} sortable={false} />
      </Datagrid>
    </>
  );
});

const ApplicationRulesFilter = (props: any) => {
  const classes = useStyles();
  const translate = useTranslate();
  const td = (key: string): string => translate(`utils.${key}`);
  return (
    <Filter {...props} className={classes.form}>
      <TextInput
        className={classes.formControl}
        label={label('applicationId')}
        source='applicationId'
        alwaysOn
      />
      <SelectInput
        className={classes.formControl}
        label={label('platform')}
        choices={[
          { id: 'ANDROID', name: label('ANDROID') },
          { id: 'IOS', name: label('IOS') },
        ]}
        source='platform'
        alwaysOn
        emptyLabel={td('any')}
      />
    </Filter>
  );
};

const useStyles = makeStyles((theme) => ({
  form: {
    '& > div': {
      '& > div:last-child': {
        width: theme.spacing(1),
      },
    },
  },
  formControl: {
    width: 200,
  },
  headline: {
    marginTop: '8px',
    display: 'flex',
  },
  multiselect: {
    width: 300,
    '& > .MuiInputBase-root ': {
      minHeight: 46,
      '& > .MuiSelect-root': {
        paddingTop: 12,
        paddingBottom: 0,
        marginBottom: -4,
      },
    },
  },
}));

export default ApplicationRulesList;
